// src/Components/Header.jsx
import React, { useState } from "react";
import {
  AppBar,
  CssBaseline,
  Toolbar,
  useScrollTrigger,
  Grid,
  CardMedia,
  Typography,
  Button,
} from "@mui/material";
import { PropTypes } from "prop-types";
import { useLocation, Link } from "react-router-dom";
import { COLORS, size } from "../utils";
import styles from "./Header.module.css";
import { FiMenu } from "react-icons/fi";

function ElevationScroll(props) {
  const { children, trigger } = props;
  return React.cloneElement(children, {
    sx: {
      backgroundColor: "transparent",
      pt: size({ p: 0.5, h: true }),
      pb: size({ p: 0.5, h: true }),
    },
    elevation: trigger ? 0 : 0,
  });
}

ElevationScroll.propTypes = {
  children: PropTypes.element.isRequired,
};

export default function Header(props) {
  const { window, menus = [] } = props;
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
    target: window ? window() : undefined,
  });
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleToggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const handleCloseMenu = () => {
    setMenuOpen(false);
  };


  return (
    <ElevationScroll {...props} trigger={trigger}>
      <AppBar
        position="sticky"
        style={{ background: COLORS.C1, backdropFilter: "blur(12px)" }}
      >
        <CssBaseline />
        <Toolbar>
          <Grid
            container
            flexWrap="nowrap"
            alignItems="center"
            justifyContent="space-between"
            gap="7rem"
            className={styles["header-container"]}
            sx={{
              paddingX: "7rem",
            }}
          >
            <Link
              to="/"
              style={{
                textDecoration: "none",
                cursor: "pointer",
                marginLeft: "-2rem"
              }}
            >
              <Grid
                item
                container
                flexWrap={"nowrap"}
                alignItems={"center"}
                columnGap={size({ p: 0.3 })}
                className={styles["header-logo"]}
              >
                <Grid item>
                  <CardMedia
                    className={styles["header-logo-1"]}
                    component={"img"}
                    image={"/assets/images/logo.svg"}
                    sx={{
                      width: size({ p: 2.8 }),
                      height: size({ p: 2.8 }),
                    }}
                  />
                </Grid>
                <Grid item>
                  <Typography
                    className={styles["header-logo-2"]}
                    style={{
                      fontFamily: "Archia",
                      fontWeight: 600,
                      fontSize: size({ p: 1.8 }),
                      color: COLORS.WHITE,
                      // paddingBottom: size({ p: 0.6, h: true }),
                    }}
                  >
                    Genesys Academy
                  </Typography>
                </Grid>
              </Grid>
            </Link>

            {/* Desktop Menu */}
            <Grid
              item
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flex: 1,
                marginLeft: size({ p: 3 }),
              }}
            >
              {/* First group - 5 menus */}
              <Grid
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: size({ p: 0.5 }),
                }}
              >
                {menus.slice(0, 5).map((menu, index) => {
                  const isExternal = !!menu.external;
                  const isButton = !!menu.button;

                  return (
                    <Grid item key={index}>
                      {isButton ? (
                        <Button
                          href={menu.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            color: COLORS.WHITE,
                            fontSize: size({ p: 0.9 }),
                            padding: `${size({ p: 0.5, h: true })} ${size({
                              p: 1.5,
                            })}`,
                            borderRadius: "20px",
                            "&:hover": {
                              backgroundColor: COLORS.C3,
                            },
                          }}
                        >
                          {menu.name}
                        </Button>
                      ) : isExternal ? (
                        <a
                          href={menu.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={handleCloseMenu}
                          style={{ textDecoration: "none" }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "Archia",
                              fontWeight: 600,
                              fontSize: size({ p: 0.9 }),
                              color: COLORS.WHITE,
                              padding: `${size({ p: 0.5, h: true })} ${size({
                                p: 0.8,
                              })}`,
                              borderRadius: size({ p: 2 }),
                              "&:hover": {
                                backgroundColor: COLORS.C3,
                              },
                            }}
                          >
                            {menu.name}
                          </Typography>
                        </a>
                      ) : (
                        <Link
                          to={menu.path}
                          onClick={handleCloseMenu}
                          style={{ textDecoration: "none" }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "Archia",
                              fontWeight: 600,
                              fontSize: size({ p: 0.9 }),
                              color: COLORS.WHITE,
                              padding: `${size({ p: 0.5, h: true })} ${size({
                                p: 0.8,
                              })}`,
                              borderRadius: size({ p: 2 }),
                              backgroundColor:
                                location.pathname === menu.path
                                  ? COLORS.C3 + "A0"
                                  : "transparent",
                              "&:hover": {
                                backgroundColor: COLORS.C3,
                              },
                            }}
                          >
                            {menu.name}
                          </Typography>
                        </Link>
                      )}
                    </Grid>
                  );
                })}
              </Grid>

              {/* Second group - 3 menus */}
              <Grid
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: size({ p: 0.5 }),
                }}
              >
                {menus.slice(5, 8).map((menu, index) => {
                  const actualIndex = index + 5;
                  const isExternal = !!menu.external;
                  const isButton = !!menu.button;

                  return (
                    <Grid item key={actualIndex}>
                      {isButton ? (
                        <Button
                          href={menu.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            color: COLORS.WHITE,
                            fontSize: size({ p: 0.9 }),
                            padding: `${size({ p: 0.5, h: true })} ${size({
                              p: 1.5,
                            })}`,
                            borderRadius: "20px",
                            "&:hover": {
                              backgroundColor: COLORS.C3,
                            },
                          }}
                        >
                          {menu.name}
                        </Button>
                      ) : isExternal ? (
                        <a
                          href={menu.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={handleCloseMenu}
                          style={{ textDecoration: "none" }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "Archia",
                              fontWeight: 600,
                              fontSize: size({ p: 0.9 }),
                              color: COLORS.WHITE,
                              padding: `${size({ p: 0.5, h: true })} ${size({
                                p: 0.8,
                              })}`,
                              borderRadius: size({ p: 2 }),
                              "&:hover": {
                                backgroundColor: COLORS.C3,
                              },
                            }}
                          >
                            {menu.name}
                          </Typography>
                        </a>
                      ) : (
                        <Link
                          to={menu.path}
                          onClick={handleCloseMenu}
                          style={{ textDecoration: "none" }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "Archia",
                              fontWeight: 600,
                              fontSize: size({ p: 0.9 }),
                              color: COLORS.WHITE,
                              padding: `${size({ p: 0.5, h: true })} ${size({
                                p: 0.8,
                              })}`,
                              borderRadius: size({ p: 2 }),
                              backgroundColor:
                                location.pathname === menu.path
                                  ? COLORS.C3 + "A0"
                                  : "transparent",
                              "&:hover": {
                                backgroundColor: COLORS.C3,
                              },
                            }}
                          >
                            {menu.name}
                          </Typography>
                        </Link>
                      )}
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>

            {/* Mobile Menu Icon */}
            <Grid className={styles["menu"]} onClick={handleToggleMenu}>
              <FiMenu />
            </Grid>
          </Grid>
        </Toolbar>

        {/* Mobile Dropdown Menu */}
        {menuOpen && (
          <Grid container direction="column" className={styles["mobile-menu"]}>
            {menus.map((menu, index) => {
              const isExternal = !!menu.external;
              const isButton = !!menu.button;

              if (isButton) {
                return (
                  <a
                    key={index}
                    href={menu.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none", width: "100%" }}
                    onClick={handleCloseMenu}
                  >
                    <Typography
                      style={{
                        fontFamily: "Archia",
                        fontWeight: 500,
                        fontSize: size({ p: 2.6 }),
                        color: COLORS.WHITE,
                        padding: size({ p: 3 }),
                        backgroundColor: COLORS.C1,
                        textAlign: "center",
                      }}
                    >
                      {menu.name}
                    </Typography>
                  </a>
                );
              }

              return isExternal ? (
                <a
                  key={index}
                  href={menu.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none", width: "100%" }}
                  onClick={handleCloseMenu}
                >
                  <Typography
                    style={{
                      fontFamily: "Archia",
                      fontWeight: 500,
                      fontSize: size({ p: 2.6 }),
                      color: COLORS.WHITE,
                      padding: size({ p: 3 }),
                      backgroundColor: COLORS.C1,
                      textAlign: "center",
                    }}
                  >
                    {menu.name}
                  </Typography>
                </a>
              ) : (
                <Link
                  key={index}
                  to={menu.path}
                  onClick={handleCloseMenu}
                  style={{ textDecoration: "none", width: "100%" }}
                >
                  <Typography
                    style={{
                      fontFamily: "Archia",
                      fontWeight: 500,
                      fontSize: size({ p: 2.6 }),
                      color: COLORS.WHITE,
                      padding: size({ p: 3 }),
                      backgroundColor:
                        location.pathname === menu.path ? COLORS.C3 + "A0" : COLORS.C1,
                      textAlign: "center",
                    }}
                  >
                    {menu.name}
                  </Typography>
                </Link>
              );
            })}
          </Grid>
        )}
      </AppBar>
    </ElevationScroll>
  );
}
